import {
    type ActionTemplateParameterResource,
    ControlType,
    type LibraryVariableSetResource,
    type ModifyTenantCommonVariableValue,
    type ModifyTenantProjectVariableValue,
    Permission,
    type ProjectResource,
    type PropertyValueResource,
    type TenantResource,
    type TenantVariableResource,
} from "@octopusdeploy/octopus-server-client";
import { generateGuid } from "~/areas/projects/components/Process/generation";
import { repository, session } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";

export interface TenantPageCommonVariableModel extends ModifyTenantCommonVariableValue {
    HasScopeChanged: boolean;
    HasValueChanged: boolean;
    Status: "overridden" | "default" | "missing" | "new";
}

export interface TenantPageProjectVariableModel extends ModifyTenantProjectVariableValue {
    HasScopeChanged: boolean;
    HasValueChanged: boolean;
    Status: "overridden" | "default" | "missing" | "new";
}

export interface PageModel {
    variables: TenantVariableResource;
    commonVariables: TenantPageCommonVariableModel[];
    projectVariables: TenantPageProjectVariableModel[];
}

export interface InitialPageData extends PageModel {
    containsAccounts: boolean;
    containsCertificates: boolean;
    containsWorkerPools: boolean;
    projects?: ProjectResource[];
    libraryVariableSets?: LibraryVariableSetResource[];
}

const accountTypes: ControlType[] = [ControlType.AmazonWebServicesAccount, ControlType.AzureAccount, ControlType.GenericOidcAccount, ControlType.GoogleCloudAccount, ControlType.UsernamePasswordAccount];
function includesAccountTypes(variables: TenantVariableResource) {
    const libraryVariableHasAccounts = Object.keys(variables.LibraryVariables).some((libraryVariableSetId) => {
        const templates = variables.LibraryVariables[libraryVariableSetId].Templates;
        return templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText));
    });
    if (libraryVariableHasAccounts) return true;

    const projectHasAccounts = Object.keys(variables.ProjectVariables).some((projectId) => {
        const templates = variables.ProjectVariables[projectId].Templates;
        return templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText));
    });
    if (projectHasAccounts) return true;
    return false;
}

function includesCertificates(variables: TenantVariableResource) {
    const libraryVariableHasCertificates = Object.keys(variables.LibraryVariables).some((libraryVariableSetId) => {
        const templates = variables.LibraryVariables[libraryVariableSetId].Templates;
        return templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.Certificate);
    });
    if (libraryVariableHasCertificates) return true;

    return Object.keys(variables.ProjectVariables).some((projectId) => {
        const templates = variables.ProjectVariables[projectId].Templates;
        return templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.Certificate);
    });
}

function includesWorkerPools(variables: TenantVariableResource) {
    const libraryVariableHasWorkerPools = Object.keys(variables.LibraryVariables).some((libraryVariableSetId) => {
        const templates = variables.LibraryVariables[libraryVariableSetId].Templates;
        return templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool);
    });
    if (libraryVariableHasWorkerPools) return true;

    return Object.keys(variables.ProjectVariables).some((projectId) => {
        const templates = variables.ProjectVariables[projectId].Templates;
        return templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool);
    });
}

interface MissingCommonTemplateMap {
    [libraryVariableSetId: string]: ActionTemplateParameterResource[];
}

interface MissingProjectTemplateMap {
    [projectId: string]: ActionTemplateParameterResource[];
}

function getMissingCommonTemplateMap(tenant: TenantResource, libraryVariableSets: LibraryVariableSetResource[], variables: ModifyTenantCommonVariableValue[]): MissingCommonTemplateMap {
    const environmentIds = Object.keys(tenant.ProjectEnvironments).flatMap((projectId) => tenant.ProjectEnvironments[projectId]);

    const missingTemplates = libraryVariableSets.map((lvs) => {
        const missingTemplates = lvs.Templates.filter((t) => {
            if (variables.some((v) => v.LibraryVariableSetId === lvs.Id && v.TemplateId === t.Id && v.Scope.EnvironmentIds.length === 0)) return false;
            return !environmentIds.every((eId) => variables.some((v) => v.LibraryVariableSetId === lvs.Id && v.TemplateId === t.Id && v.Scope.EnvironmentIds.includes(eId)));
        });
        return { libraryVariableSetId: lvs.Id, missingTemplates };
    });
    return missingTemplates.reduce((result, { libraryVariableSetId, missingTemplates }) => ({ ...result, [libraryVariableSetId]: missingTemplates }), {});
}

function getMissingProjectTemplateMap(tenant: TenantResource, projects: ProjectResource[], variables: ModifyTenantProjectVariableValue[]): MissingProjectTemplateMap {
    const result: MissingProjectTemplateMap = {};

    projects.forEach((project) => {
        const projectEnvironmentIds = tenant.ProjectEnvironments[project.Id] || [];
        const missingTemplates = project.Templates.filter((template) => {
            if (variables.some((v) => v.ProjectId === project.Id && v.TemplateId === template.Id && v.Scope.EnvironmentIds.length === 0)) return false;
            return !projectEnvironmentIds.every((eId) => variables.some((v) => v.ProjectId === project.Id && v.TemplateId === template.Id && v.Scope.EnvironmentIds.includes(eId)));
        });

        if (missingTemplates.length > 0) {
            result[project.Id] = missingTemplates;
        }
    });

    return result;
}

function generateCommonVariable(libraryVariableSet: LibraryVariableSetResource, template: ActionTemplateParameterResource): TenantPageCommonVariableModel {
    return {
        Id: generateGuid(),
        LibraryVariableSetId: libraryVariableSet.Id,
        TemplateId: template.Id,
        Value: null,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: template.DefaultValue ? "default" : "missing",
        Scope: {
            EnvironmentIds: [],
        },
    };
}

function generateProjectVariable(project: ProjectResource, template: ActionTemplateParameterResource): TenantPageProjectVariableModel {
    return {
        Id: generateGuid(),
        ProjectId: project.Id,
        TemplateId: template.Id,
        Value: null,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: template.DefaultValue ? "default" : "missing",
        Scope: {
            EnvironmentIds: [],
        },
    };
}

async function getModelFromApi(tenant: TenantResource): Promise<InitialPageData> {
    if (!isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
        const variables = await repository.Tenants.getVariables(tenant);

        return {
            variables,
            commonVariables: [],
            projectVariables: [],
            containsAccounts: includesAccountTypes(variables),
            containsCertificates: includesCertificates(variables),
            containsWorkerPools: includesWorkerPools(variables),
        };
    }

    // This is a temporary implementation to maintain the existing model structure
    const variables: TenantVariableResource = {
        Id: "",
        TenantId: tenant.Id,
        TenantName: tenant.Name,
        SpaceId: tenant.SpaceId,
        Links: {},
        LibraryVariables: {},
        ProjectVariables: {},
    };

    const projectVariables: TenantPageProjectVariableModel[] = (await repository.Tenants.getProjectVariablesByTenantId(tenant.Id)).Variables.map((v) => ({
        ...v,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: "overridden",
    }));

    const projectIds = Object.keys(tenant.ProjectEnvironments);
    const authorizedProjectIds = projectIds.filter((projectId) => {
        return session.currentPermissions?.scopeToSpace(repository.spaceId).isAuthorized({
            permission: Permission.ProjectView,
            projectId: projectId,
        });
    });

    const projects = await Promise.all(authorizedProjectIds.map((projectId) => repository.Projects.get(projectId)));

    variables.ProjectVariables = projects.reduce(
        (result, project) => ({
            ...result,
            [project.Id]: {
                ProjectId: project.Id,
                ProjectName: project.Name,
                Templates: project.Templates,
                Variables: tenant.ProjectEnvironments[project.Id].reduce((variables, envId) => ({ ...variables, [envId]: {} }), {}),
                TenantVariables: {},
                Links: project.Links,
            },
        }),
        {}
    );
    projectVariables.forEach((projectVariable) => {
        const projectId = projectVariable.ProjectId;
        const environmentId = projectVariable.Scope.EnvironmentIds[0];
        const templateId = projectVariable.TemplateId;
        variables.ProjectVariables[projectId].Variables[environmentId] = {
            ...variables.ProjectVariables[projectId].Variables[environmentId],
            [templateId]: projectVariable.Value,
        };
    });

    const commonVariables: TenantPageCommonVariableModel[] = (await repository.Tenants.getCommonVariablesByTenantId(tenant.Id)).Variables.map((v) => ({
        ...v,
        HasScopeChanged: false,
        HasValueChanged: false,
        Status: "overridden",
    }));
    const libraryVariableSetIds = Array.from(new Set(projects.flatMap((project) => project.IncludedLibraryVariableSetIds)));
    const libraryVariableSets = await Promise.all(libraryVariableSetIds.map((libraryVariableSetId) => repository.LibraryVariableSets.get(libraryVariableSetId)));

    variables.LibraryVariables = {};

    const containsAccounts =
        libraryVariableSets.some((s) => s.Templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText))) ||
        projects.some((p) => p.Templates.some((t) => accountTypes.includes(t.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText)));

    const containsCertificates =
        libraryVariableSets.some((s) => s.Templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.Certificate)) ||
        projects.some((p) => p.Templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.Certificate));

    const containsWorkerPools =
        libraryVariableSets.some((s) => s.Templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool)) ||
        projects.some((p) => p.Templates.some((t) => t.DisplaySettings["Octopus.ControlType"] === ControlType.WorkerPool));

    const missingCommonTemplateMap = getMissingCommonTemplateMap(tenant, libraryVariableSets, commonVariables);

    libraryVariableSets.forEach((lvs) => {
        const missingTemplates = missingCommonTemplateMap[lvs.Id] ?? [];
        missingTemplates.forEach((template) => {
            commonVariables.push(generateCommonVariable(lvs, template));
        });
    });

    const missingProjectTemplateMap = getMissingProjectTemplateMap(tenant, projects, projectVariables);

    Object.keys(missingProjectTemplateMap).forEach((projectId) => {
        const project = projects.find((p) => p.Id === projectId);
        if (!project) return;

        const missingTemplates = missingProjectTemplateMap[projectId];
        missingTemplates.forEach((template) => {
            projectVariables.push(generateProjectVariable(project, template));
        });
    });

    return { variables, commonVariables, projectVariables, projects, libraryVariableSets, containsAccounts, containsCertificates, containsWorkerPools };
}

async function sendModelToApi(tenant: TenantResource, model: PageModel): Promise<void> {
    if (!isFeatureToggleEnabled("CommonVariableScopingFeatureToggle")) {
        await repository.Tenants.setVariables(tenant, model.variables);
        return;
    }

    await repository.Tenants.setProjectVariablesByTenantId(tenant.Id, {
        TenantId: tenant.Id,
        Variables: model.projectVariables.filter((v) => v.Value !== null),
    });

    await repository.Tenants.setCommonVariablesByTenantId(tenant.Id, {
        TenantId: tenant.Id,
        Variables: model.commonVariables.filter((v) => v.Value !== null),
    });
}

function updateModelWithCommonVariable(model: PageModel, libraryVariableSetId: string, templateId: string, value: PropertyValueResource): PageModel {
    return {
        ...model,
        variables: {
            ...model.variables,
            LibraryVariables: {
                ...model.variables.LibraryVariables,
                [libraryVariableSetId]: {
                    ...model.variables.LibraryVariables[libraryVariableSetId],
                    Variables: {
                        ...model.variables.LibraryVariables[libraryVariableSetId].Variables,
                        [templateId]: value,
                    },
                },
            },
        },
    };
}

export type ModelAction = UpdateValueModelAction | UpdateScopeModelAction | AddModelAction | RemoveModelAction;
export type ProjectVariableModelAction = UpdateValueModelAction | UpdateScopeModelAction | AddProjectVariableModelAction | RemoveModelAction;

type UpdateValueModelAction = {
    action: "updateValue";
    variableId: string;
    value: PropertyValueResource;
};
type UpdateScopeModelAction = {
    action: "updateScope";
    variableId: string;
    environmentIds: string[];
};
type AddModelAction = {
    action: "add";
    templateId: string;
    libraryVariableSetId: string;
};
type AddProjectVariableModelAction = {
    action: "addProjectVariable";
    templateId: string;
    projectId: string;
};
type RemoveModelAction = {
    action: "remove";
    variableId: string;
};

function updateModelWithCommonVariableV2(model: PageModel, action: ModelAction): PageModel {
    switch (action.action) {
        case "updateValue":
            return updateCommonVariableModelValue(model, action.variableId, action.value);
        case "updateScope":
            return updateCommonVariableModelScope(model, action.variableId, action.environmentIds);
        case "add":
            return addCommonVariableModel(model, action.libraryVariableSetId, action.templateId);
        case "remove":
            return removeCommonVariableModel(model, action.variableId);
    }
}

function updateModelWithProjectVariableV2(model: PageModel, action: ProjectVariableModelAction): PageModel {
    switch (action.action) {
        case "updateValue":
            return updateProjectVariableModelValue(model, action.variableId, action.value);
        case "updateScope":
            return updateProjectVariableModelScope(model, action.variableId, action.environmentIds);
        case "addProjectVariable":
            return addProjectVariableModel(model, action.projectId, action.templateId);
        case "remove":
            return removeProjectVariableModel(model, action.variableId);
    }
}

function updateCommonVariableModelValue(model: PageModel, variableId: string, value: PropertyValueResource): PageModel {
    return {
        ...model,
        commonVariables: model.commonVariables.map((c) => (c.Id === variableId ? { ...c, Value: value, HasValueChanged: true } : c)),
    };
}

function updateProjectVariableModelValue(model: PageModel, variableId: string, value: PropertyValueResource): PageModel {
    return {
        ...model,
        projectVariables: model.projectVariables.map((c) => (c.Id === variableId ? { ...c, Value: value, HasValueChanged: true } : c)),
    };
}

function updateCommonVariableModelScope(model: PageModel, variableId: string, environmentIds: string[]): PageModel {
    return {
        ...model,
        commonVariables: model.commonVariables.map((c) => (c.Id === variableId ? { ...c, Scope: { EnvironmentIds: environmentIds }, HasScopeChanged: true } : c)),
    };
}

function updateProjectVariableModelScope(model: PageModel, variableId: string, environmentIds: string[]): PageModel {
    return {
        ...model,
        projectVariables: model.projectVariables.map((c) => (c.Id === variableId ? { ...c, Scope: { EnvironmentIds: environmentIds }, HasScopeChanged: true } : c)),
    };
}

function addCommonVariableModel(model: PageModel, libraryVariableSetId: string, templateId: string): PageModel {
    return {
        ...model,
        commonVariables: [
            ...model.commonVariables,
            {
                Id: generateGuid(),
                LibraryVariableSetId: libraryVariableSetId,
                TemplateId: templateId,
                Value: null,
                HasScopeChanged: true,
                HasValueChanged: true,
                Status: "new",
                Scope: {
                    EnvironmentIds: [],
                },
            },
        ],
    };
}

function addProjectVariableModel(model: PageModel, projectId: string, templateId: string): PageModel {
    return {
        ...model,
        projectVariables: [
            ...model.projectVariables,
            {
                Id: generateGuid(),
                ProjectId: projectId,
                TemplateId: templateId,
                Value: null,
                HasScopeChanged: true,
                HasValueChanged: true,
                Status: "new",
                Scope: {
                    EnvironmentIds: [],
                },
            },
        ],
    };
}

function removeCommonVariableModel(model: PageModel, variableId: string): PageModel {
    const removedVariable = model.commonVariables.find((c) => c.Id === variableId);
    if (removedVariable === undefined) return model;
    const variablesWithoutVariable = model.commonVariables.filter((c) => c.Id !== variableId);
    const hasRemaining = variablesWithoutVariable.some((c) => c.LibraryVariableSetId === removedVariable.LibraryVariableSetId && c.TemplateId === removedVariable.TemplateId);
    if (!hasRemaining) {
        variablesWithoutVariable.push({
            Id: generateGuid(),
            LibraryVariableSetId: removedVariable.LibraryVariableSetId,
            TemplateId: removedVariable.TemplateId,
            Value: null,
            HasScopeChanged: true,
            HasValueChanged: true,
            Status: "new",
            Scope: {
                EnvironmentIds: [],
            },
        });
    }
    return {
        ...model,
        commonVariables: variablesWithoutVariable,
    };
}

function removeProjectVariableModel(model: PageModel, variableId: string): PageModel {
    const removedVariable = model.projectVariables.find((c) => c.Id === variableId);
    if (removedVariable === undefined) return model;
    const variablesWithoutVariable = model.projectVariables.filter((c) => c.Id !== variableId);
    const hasRemaining = variablesWithoutVariable.some((c) => c.ProjectId === removedVariable.ProjectId && c.TemplateId === removedVariable.TemplateId);
    if (!hasRemaining) {
        variablesWithoutVariable.push({
            Id: generateGuid(),
            ProjectId: removedVariable.ProjectId,
            TemplateId: removedVariable.TemplateId,
            Value: null,
            HasScopeChanged: true,
            HasValueChanged: true,
            Status: "new",
            Scope: {
                EnvironmentIds: [],
            },
        });
    }
    return {
        ...model,
        projectVariables: variablesWithoutVariable,
    };
}

function updateModelWithProjectVariable(model: PageModel, projectId: string, environmentId: string, templateId: string, value: PropertyValueResource): PageModel {
    const newModel = {
        ...model,
        variables: {
            ...model.variables,
            ProjectVariables: {
                ...model.variables.ProjectVariables,
                [projectId]: {
                    ...model.variables.ProjectVariables[projectId],
                    Variables: {
                        ...model.variables.ProjectVariables[projectId].Variables,
                        [environmentId]: {
                            ...model.variables.ProjectVariables[projectId].Variables[environmentId],
                            [templateId]: value,
                        },
                    },
                },
            },
        },
    };
    return newModel;
}

export { getModelFromApi, sendModelToApi, updateModelWithCommonVariable, updateModelWithCommonVariableV2, updateModelWithProjectVariable, updateModelWithProjectVariableV2 };
